import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ImageModal } from './workspace/ImageModal';
import { JobListItem } from './ui/JobListItem';

/**
 * Calculate optimal page size based on viewport height.
 *
 * The goal is to fill the visible area without requiring scroll,
 * providing a clean initial view that shows as many items as fit.
 *
 * Layout considerations:
 * - Navigation bar: ~64px
 * - List header ("My Uploads"): ~70px
 * - Margins/padding: ~50px
 * - Load More button area: ~60px
 *
 * Item heights (from JobListItem.jsx):
 * - Mobile (<640px): ~96px per item
 * - Desktop (>=640px): ~128px per item
 */
const calculatePageSize = () => {
  const viewportHeight = window.innerHeight;
  const headerAndNav = 180; // nav + list header + margins
  const loadMoreArea = 60;  // space for load more button
  const availableHeight = viewportHeight - headerAndNav - loadMoreArea;

  // Determine item height based on screen width (mobile vs desktop)
  const isMobile = window.innerWidth < 640;
  const itemHeight = isMobile ? 96 : 128;

  // Calculate how many items fit in the available space
  const fittingItems = Math.floor(availableHeight / itemHeight);

  // Clamp between min (3) and max (8) to avoid extremes
  const minItems = 3;
  const maxItems = 8;
  return Math.max(minItems, Math.min(maxItems, fittingItems));
};

// Resume Modal Component
const ResumeModal = ({ job, onResume, onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className="text-center space-y-6">
          <div className="text-5xl">🔄</div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-gray-900">Continue creating?</h3>
            <p className="text-gray-600">
              Pick up where you left off
            </p>
          </div>

          {/* Resume button */}
          <button
            onClick={onResume}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export const UserHistory = () => {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();

  // Job data state
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Pagination state - calculate initial page size based on viewport
  const [pageSize] = useState(() => calculatePageSize());

  // Modal state (for completed jobs only)
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Resume modal state (for canceled jobs)
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeJob, setResumeJob] = useState(null);

  /**
   * Fetch job history from API with server-side pagination.
   *
   * @param {number} offset - Starting offset for pagination
   * @param {boolean} append - If true, append to existing jobs; if false, replace
   * @returns {Promise<Object|null>} Response data or null on error
   */
  const fetchHistory = useCallback(async (offset = 0, append = false) => {
    if (!user) {
      setError('Please sign in to view your history');
      setLoading(false);
      return null;
    }

    try {
      const token = await getIdToken();
      const response = await axios.get('/api/v1/jobs/user/history', {
        params: {
          limit: pageSize,
          offset: offset
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const { jobs: newJobs, total } = response.data;

      // Update total count (always use latest from server)
      setTotalJobs(total);

      if (append) {
        // Append new jobs to existing list (Load More)
        setJobs(prevJobs => [...prevJobs, ...newJobs]);
      } else {
        // Replace jobs (initial load or refresh)
        setJobs(newJobs);
      }

      setLoading(false);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError(err.response?.data?.detail || 'Failed to fetch history');
      setLoading(false);
      return null;
    }
  }, [user, getIdToken, pageSize]);

  // Initial load - fetch on mount
  // This ensures fresh data when navigating back to history page
  // (e.g., after canceling a job from the processing view)
  useEffect(() => {
    fetchHistory(0, false);
  }, [fetchHistory]); // Re-fetch when fetchHistory changes (e.g., user changes)

  // Auto-refresh for in-progress jobs
  // Only refreshes the currently loaded jobs (first page) to update status
  useEffect(() => {
    // Check if there are any active jobs (processing or queued)
    const hasActiveJobs = jobs.some(
      job => job.status === 'processing' || job.status === 'queued'
    );

    if (!hasActiveJobs || !user) {
      // No active jobs, no need to poll
      return;
    }

    console.log('[UserHistory] Active jobs detected, starting auto-refresh');

    // Poll every 5 seconds for status updates
    // Note: For simplicity, we refresh from offset 0 with enough limit to cover loaded jobs
    const interval = setInterval(async () => {
      console.log('[UserHistory] Auto-refreshing job statuses');
      // Fetch all currently loaded jobs to update their status
      const token = await getIdToken();
      try {
        const response = await axios.get('/api/v1/jobs/user/history', {
          params: {
            limit: jobs.length || pageSize,  // Fetch at least as many as currently loaded
            offset: 0
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const { jobs: refreshedJobs, total } = response.data;
        setJobs(refreshedJobs);
        setTotalJobs(total);
      } catch (err) {
        console.error('[UserHistory] Auto-refresh failed:', err);
      }
    }, 5000);

    return () => {
      console.log('[UserHistory] Stopping auto-refresh');
      clearInterval(interval);
    };
  }, [jobs.length, user, getIdToken, pageSize]);

  // Pagination calculations
  const hasMore = jobs.length < totalJobs;
  const remainingJobs = totalJobs - jobs.length;

  // Handler: Load More button - fetch next page from server
  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    const nextOffset = jobs.length;  // Start from where we left off
    await fetchHistory(nextOffset, true);  // Append new jobs
    setIsLoadingMore(false);
  };

  // Handler: Job click - navigate or open modal based on status
  const handleJobClick = (job) => {
    console.log('[UserHistory] Job clicked:', job.id, 'Status:', job.status);

    // For in-progress or queued jobs, navigate to WorkspaceDashboard with job context
    if (job.status === 'processing' || job.status === 'queued') {
      console.log('[UserHistory] Navigating to processing view for job:', job.id);
      // Navigate to home page which will show the processing view
      navigate('/', { state: { jobId: job.id } });
      return;
    }

    // For canceled jobs, show resume prompt
    if (job.status === 'canceled') {
      console.log('[UserHistory] Opening resume prompt for canceled job:', job.id);
      setResumeJob(job);
      setResumeModalOpen(true);
      return;
    }

    // For completed jobs, open modal
    if (job.status === 'completed') {
      console.log('[UserHistory] Opening modal for completed job:', job.id);
      setSelectedJob(job);
      setModalImageIndex(0);
      setModalOpen(true);
      return;
    }

    // For failed jobs, could navigate to error details (future enhancement)
    console.log('[UserHistory] Job status:', job.status, '- no action defined');
  };

  // Handler: Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
    setModalImageIndex(0);
  };

  // Handler: Resume canceled job
  const handleResumeJob = async () => {
    if (!resumeJob) return;

    try {
      console.log('[UserHistory] Resuming canceled job:', resumeJob.id);

      // First, check current job status (it might have changed)
      const token = await getIdToken();
      const jobResponse = await axios.get(`/api/v1/jobs/${resumeJob.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const currentJob = jobResponse.data;

      setResumeModalOpen(false);

      // If job is already completed, just show the results
      if (currentJob.status === 'completed') {
        console.log('[UserHistory] Job already completed, showing results');
        setSelectedJob(currentJob);
        setModalImageIndex(0);
        setModalOpen(true);
        setResumeJob(null);
        return;
      }

      // If job is processing or queued, navigate to processing view
      if (currentJob.status === 'processing' || currentJob.status === 'queued') {
        console.log('[UserHistory] Job already in progress');
        navigate('/', { state: { jobId: resumeJob.id } });
        setResumeJob(null);
        return;
      }

      // If still canceled, resume it
      if (currentJob.status === 'canceled') {
        await axios.post(`/api/v1/jobs/${resumeJob.id}/resume`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        console.log('[UserHistory] Job resumed successfully');
        navigate('/', { state: { jobId: resumeJob.id } });
        setResumeJob(null);
      }
    } catch (error) {
      console.error('[UserHistory] Failed to resume job:', error);
      setResumeModalOpen(false);
      setResumeJob(null);
      // Silently fail - user can try again from history
    }
  };

  // Handler: Close resume modal
  const handleCloseResumeModal = () => {
    setResumeModalOpen(false);
    setResumeJob(null);
  };

  // Handler: Order button (placeholder for now)
  const handleOrder = () => {
    if (selectedJob) {
      console.log('[UserHistory] Order clicked for job:', selectedJob.id);
      alert('Order functionality coming soon!');
    }
  };

  // Get display images for modal (same format as WorkspaceDashboard)
  const getDisplayImages = (job) => {
    if (!job || !job.processing_results) return [];

    const results = job.processing_results;
    const images = [];

    // Original user photo
    if (job.image_url) {
      images.push({
        url: job.image_url,
        label: 'Your Photo',
      });
    }

    // Markup/design preview (prefer whole image, fall back to undershirt)
    const markupUrl = results.markup_whole_url || results.markup_undershirt_url;
    if (markupUrl) {
      images.push({
        url: markupUrl,
        label: 'Design Preview',
      });
    }

    // Rendered 3D model
    if (results.model_image_url) {
      images.push({
        url: results.model_image_url,
        label: '3D Model',
      });
    }

    return images;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6">
      {jobs.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Uploads Yet</h2>
          <p className="text-gray-600 px-4">
            You haven't created any models yet. Start by uploading a photo!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Jobs List */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                My Uploads
              </h2>
              {totalJobs > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Showing {jobs.length} of {totalJobs}
                </p>
              )}
            </div>

            <div className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <JobListItem
                  key={job.id}
                  job={job}
                  onClick={handleJobClick}
                />
              ))}
            </div>
          </div>

          {/* Load More / All Loaded indicator */}
          {hasMore ? (
            <div className="flex justify-center pb-4">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="px-6 py-3 bg-white border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isLoadingMore ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700"></div>
                    <span>Loading...</span>
                  </div>
                ) : (
                  <span>Load More ({remainingJobs} remaining)</span>
                )}
              </button>
            </div>
          ) : (
            totalJobs > pageSize && (
              <div className="text-center pb-4">
                <p className="text-sm text-gray-500">All {totalJobs} uploads loaded</p>
              </div>
            )
          )}
        </div>
      )}

      {/* Image Modal (for completed jobs only) */}
      {modalOpen && selectedJob && (
        <ImageModal
          images={getDisplayImages(selectedJob)}
          currentIndex={modalImageIndex}
          onClose={handleCloseModal}
          onIndexChange={setModalImageIndex}
          onOrder={handleOrder}
        />
      )}

      {/* Resume Modal (for canceled jobs) */}
      {resumeModalOpen && resumeJob && (
        <ResumeModal
          job={resumeJob}
          onResume={handleResumeJob}
          onClose={handleCloseResumeModal}
        />
      )}
    </div>
  );
};
